import { useState, useEffect } from "react";
import useSWR from "swr";
import { useRouter } from "next/router";
import {
  get,
  round,
  groupBy,
  map,
  replace,
  isNull,
  find,
  truncate,
  isEmpty,
  upperFirst,
  capitalize,
} from "lodash";
import { productPage } from "grandus-lib/utils/fetches";
import { reqGetHeadersFront } from "grandus-lib/utils";
import EnhancedEcommerce from "utils/ecommerce";
import TagManager from "grandus-lib/utils/gtag";

import styles from "./product.page.module.scss";

import dynamic from "next/dynamic";
import useWishlist from "grandus-lib/hooks/useWishlist";
import useCompare from "grandus-lib/hooks/useCompare";
import MetaData from "grandus-lib/components-atomic/MetaData";
import Breadcrumbs from "components/breadcrumbs/Breadcrumbs";
import AddToCartButton from "components/product/detail/AddToCartButton";
import Retails from "components/product/detail/Retails";
import TimeToArrival from "components/product/detail/TimeToArrival";
import ProductLabels from "components/product/card/ProductLabels";
const CrossSale = dynamic(() => import("components/product/detail/crossSale"), {
  ssr: false,
});
const AlternativeProducts = dynamic(
  () => import("components/product/detail/alternativeProducts"),
  {
    ssr: false,
  }
);
import ProductCondition from "./partials/ProductCondition";
import ProductQuatro from "./partials/ProductQuatro";
import ProductPriceVariants from "./partials/ProductPriceVariants";

import Variant from "grandus-lib/components/product/variant/Variant.default";
import Hook from "grandus-lib/components/product/hook/Hook.default";
import Gallery from "components/gallery/Gallery";
import MobileGallery from "components/gallery/MobileGallery";
import { message } from "antd";
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';

const CompareButton = ({ productId }) => {
  const { t } = useTranslation();
  const { isLoading, itemAdd, itemRemove, itemExists } = useCompare();
  const key = `message-compare-${productId}`;
  const text = isLoading
    ? `${t("common:loading")}...`
    : !itemExists(productId)
    ? upperFirst(t("product:add product to compare"))
    : upperFirst(t("product:remove product from compare"));

  return (
    <span
      className={`${styles.actionButton} ${
        itemExists(productId) ? styles.active : ""
      }`}
      onClick={(e) => {
        if (!isLoading) {
          if (itemExists(productId)) {
            message.loading({
              content: upperFirst(t("product:removing product from compare")),
              key,
            });
            itemRemove(productId, () => {
              message.success({
                content: upperFirst(
                  t("product:product was removed from compare")
                ),
                key,
              });
            });
          } else {
            message.loading({
              content: upperFirst(t("product:adding product to compare")),
              key,
            });
            itemAdd(productId, (result) => {
              message.success({
                content: upperFirst(t("product:product was added to compare")),
                key,
              });
            });
          }
        }
      }}
    >
      <svg
        height={32}
        width={32}
        viewBox="0 0 32 32"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>compare</title>
        <g>
          <path d="M29.196 25.507a5.992 5.992 0 0 0 1.6-3.225c.042-.253.05-.514.067-.775.009-.134.009-.286.034-.42a.514.514 0 0 0-.06-.253l-.109-.287-2.635-6.391c-.876-2.122-1.76-4.261-2.645-6.383a3.25 3.25 0 0 0-.143-.337.918.918 0 0 0-.91-.497h-.008c-.084.008-.21.008-.328 0l-.91-.076c-.32-.025-.64-.05-.96-.084l-.951-.076-.716-.05-.783-.06a.539.539 0 0 1-.497-.395l-.008-.017a2.337 2.337 0 0 0-.329-.665c-.783-1.137-1.827-1.634-3.191-1.508-.935.085-1.685.497-2.308 1.28-.11.127-.177.245-.227.346-.084.177-.286.454-.75.379a2.302 2.302 0 0 0-.42-.042l-.885-.076c-.295-.026-.59-.05-.876-.076l-1.103-.093c-.606-.05-1.238-.118-1.852-.16-.464-.033-.784.152-.977.548l-.009.025-.1.185-1.079 2.62c-1.22 2.963-2.484 6.029-3.73 9.043-.06.16-.126.304-.185.472-.202.472-.396.96-.548 1.457 0 .008-.008.008-.017.017v.042c.009.151.009.286.026.438.008.092.033.176.042.278.008.092.025.176.05.277.245 1.432.935 2.661 2.089 3.638a6.361 6.361 0 0 0 3.377 1.5c2.433.328 4.808-.658 6.197-2.57.7-.96 1.07-1.978 1.146-3.115.008-.076.008-.135.017-.202.008-.11.008-.219.025-.337a1.036 1.036 0 0 0-.11-.58l-1.911-4.64c-.85-2.073-1.735-4.211-2.61-6.325-.01-.009-.01-.025-.018-.042-.042-.093-.11-.236-.11-.413l.026-.202.177-.101a.525.525 0 0 1 .387-.05h.017l1.684.143c.329.033.657.05.986.084.033 0 .05.008.084 0 .042.008.084.008.126.025h.026c.286.008.496.168.606.438.471 1.145 1.314 1.81 2.492 2.004 1.415.236 2.56-.252 3.403-1.448l.008-.009c.025-.025.042-.05.042-.075.076-.16.244-.396.657-.363.64.06 1.28.11 1.912.152l.075.017c.152.008.287.025.421.042l.421.025a.464.464 0 0 1 .38.202c.084.127.084.278.033.43l-.093.252-1.002 2.527c-1.162 2.82-2.366 5.751-3.562 8.623-.05.151-.11.295-.143.446a.741.741 0 0 1-.042.127 1.128 1.128 0 0 0-.034.42 5.819 5.819 0 0 0 1.137 3.26c.969 1.347 2.316 2.197 3.983 2.56.169.042.337.058.514.084.059.008.118.025.185.033h.034c.042.009.11.009.177.043h.926c.034-.009.059-.034.101-.043h.017c.152-.025.295-.05.413-.067a6.351 6.351 0 0 0 3.764-1.954zm-17.583-4.783l-.042.37c-.085.658-.598 1.525-1.264 2.131a4.492 4.492 0 0 1-3.006 1.196h-.143c-2.089 0-4.034-1.465-4.404-3.318l-.085-.404.438-.034c.43-.033 7.882-.025 8.135 0l.37.06zM9.97 15.545c.378.918.757 1.844 1.136 2.762l.085.186-.127.185a.518.518 0 0 1-.446.219c-.025 0-.042 0-.076-.009H3.975c-.236 0-.447 0-.573-.202-.135-.202-.05-.395.017-.598.556-1.347 1.103-2.686 1.667-4.033.556-1.34 1.103-2.67 1.66-4.026.025-.05.041-.109.067-.134l.025-.084.16-.371.37.185a.37.37 0 0 1 .169.11l.05.042.034.076c.783 1.894 1.566 3.831 2.35 5.692zm7.536-8.193c-.084.58-.555 1.052-1.153 1.153-.093.026-.177.026-.27.034a1.44 1.44 0 0 1-1.423-1.44c.008-.396.202-.766.505-1.01.278-.22.59-.329.935-.329.387-.008.8.168 1.07.472.277.294.387.699.336 1.12zm3.158 12.084l.017-.042c.446-1.087.901-2.215 1.373-3.343.328-.784.64-1.55.951-2.325.32-.766.632-1.532.952-2.29.025-.06.05-.118.084-.185.025-.043.034-.076.042-.102l.22-.513.403.396.009.008c.033.034.084.084.126.16l.017.025.783 1.912c.876 2.122 1.794 4.337 2.67 6.492v.034c.042.126.008.236 0 .278l-.05.295h-7.58l-.084-.253c-.084-.252.025-.455.067-.547zm3.647 6.34c-1.188 0-2.283-.47-3.25-1.414-.43-.43-.767-.96-.978-1.575-.042-.084-.05-.168-.084-.26l-.084-.33.286-.134a.735.735 0 0 1 .438-.059h7.537c.202 0 .42 0 .556.194s.05.413-.009.59c-.513 1.38-1.473 2.307-2.855 2.736a5.188 5.188 0 0 1-1.557.253z" />
        </g>
      </svg>
      <span>{text}</span>
    </span>
  );
};

const WishlistButton = ({ productId }) => {
  const { t } = useTranslation();
  const { isLoading, itemAdd, itemRemove, itemExists } = useWishlist();
  const key = `message-wishlist-${productId}`;
  const text = isLoading
    ? `${t("common:loading")}...`
    : !itemExists(productId)
    ? upperFirst(t("product:add product to wishlist"))
    : upperFirst(t("product:remove product from wishlist"));

  return (
    <span
      className={`${styles.actionButton} ${
        itemExists(productId) ? styles.active : ""
      }`}
      onClick={(e) => {
        if (!isLoading) {
          if (itemExists(productId)) {
            message.loading({
              content: upperFirst(t("product:removing product from wishlist")),
              key,
            });
            itemRemove(productId, () => {
              message.success({
                content: upperFirst(
                  t("product:product was removed from wishlist")
                ),
                key,
              });
            });
          } else {
            message.loading({
              content: upperFirst(t("product:adding product to wishlist")),
              key,
            });
            itemAdd(productId, (result) => {
              if (get(result, "errorMessage")) {
                message.error({
                  content: get(result, "errorMessage"),
                  key,
                });
              } else {
                message.success({
                  content: upperFirst(
                    t("product:product was added to wishlist")
                  ),
                  key,
                });
              }
            });
          }
        }
      }}
    >
      <svg
        height={32}
        width={32}
        viewBox="0 0 32 32"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>favourite</title>
        <g>
          <path d="M15.377 6.585C14.063 5.5 12.556 4.96 10.77 4.943c-.683.009-1.255.076-1.853.244-1.583.421-2.855 1.255-3.781 2.476-1.044 1.373-1.432 2.922-1.145 4.632.117.75.345 1.507.69 2.316.446 1.035 1.044 2.088 1.844 3.208a32.86 32.86 0 0 0 3.066 3.688c1.625 1.685 3.31 3.209 5.019 4.522.202.152.378.287.564.413.21.135.463.202.75.169.21-.026.395-.118.614-.287 2.063-1.642 3.832-3.242 5.415-4.892 1.313-1.356 2.374-2.636 3.242-3.883.74-1.052 1.28-2.02 1.684-2.964.421-.968.657-1.878.75-2.737a5.84 5.84 0 0 0-.337-2.635c-.556-1.474-1.584-2.61-3.024-3.394-1.448-.783-3.04-1.044-4.715-.766A7.046 7.046 0 0 0 16.21 6.56l-.026.017-.092.076-.017.016-.025.017a.494.494 0 0 1-.59-.033l-.084-.068zm.404 2.77c.328 0 .632-.15.825-.395.026-.025.034-.042.06-.076.025-.033.041-.059.058-.084.809-1.12 1.929-1.794 3.318-1.987 1.432-.21 2.729.126 3.849.985.934.716 1.515 1.65 1.717 2.787.076.447.076.901 0 1.423-.11.708-.328 1.415-.707 2.224-.421.9-.977 1.819-1.701 2.812a32.788 32.788 0 0 1-2.855 3.335 52.64 52.64 0 0 1-4.1 3.79l-.026.008c-.034.025-.076.05-.093.084-.042.042-.092.076-.134.11l-.16.109-.177-.05c-.16-.043-.261-.135-.337-.203a.143.143 0 0 0-.042-.025h-.017a45.053 45.053 0 0 1-4.135-3.831 30.318 30.318 0 0 1-2.871-3.445c-.708-.985-1.238-1.903-1.642-2.804-.337-.74-.531-1.39-.632-2.038-.261-1.777.328-3.233 1.743-4.328.842-.657 1.861-.985 3.023-.985a4.96 4.96 0 0 1 3.082 1.01c.37.278.682.598.96.985l.009.009a.825.825 0 0 0 .143.193l.008.009c.21.244.514.379.834.379z" />
        </g>
      </svg>
      <span>{text}</span>
    </span>
  );
};

const NoVATMessage = () => {
  const { data: staticBlocks } = useSWR(
    `/api/lib/v1/blocks`,
    (url) => fetch(url).then((r) => r.json()),
    {
      revalidateOnReconnect: false,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  const message = get(
    find(staticBlocks, ["hash", "product_no_vat_message"]),
    "content",
    ""
  );

  if (isEmpty(message)) {
    return null;
  }

  return (
    <div dangerouslySetInnerHTML={{ __html: message }} />
  );
}

const Product = (props) => {
  const { t } = useTranslation();
  const [showedProductBlock, setShowedProductBlock] = useState("description");
  const router = useRouter();
  const { data: product, error } = useSWR(
    `/api/lib/v1/products/${get(router, "query.id")}`,
    (url) =>
      fetch(url, {
        headers: reqGetHeadersFront(null, { forwardUrl: router.asPath }),
      }).then((r) => r.json()),
    {
      initialData: props.product,
      revalidateOnMount: true,
    }
  );
  useEffect(() => {
    TagManager.push(EnhancedEcommerce.viewItem(product));
  }, []);

  const groupedParameters = groupBy(
    get(product, "detailedParameters", []),
    (parameter) => parameter.group
  );

  const isUnavailable =
    !get(product, "active") ||
    (!get(product, "availability") &&
      isNull(get(product, "storeStatus.deliveryDate")) &&
      !(find(
        get(product, "store", []),
        (storeEntry) => get(storeEntry, "count", 0) > 0
      )
        ? true
        : false));

  const breadcrumbs = { current: get(product, "name") };
  if (get(product, "breadcrumbs")) {
    breadcrumbs.data = get(product, "breadcrumbs");
  }

  return (
    <div id={get(styles, "product-detail")}>
      <div className={styles.container}>
        <MetaData
          {...get(product, "meta", {})}
          title={`${truncate(get(product, "name"), { length: 35 })} (${get(
            product,
            "sku"
          )}) za ${get(product, "finalPriceData.priceFormatted")}`}
          description={
            truncate(
              get(
                product,
                "meta.description",
                get(product, "shortProductDescription.description")
              ),
              { length: 146 }
            ) + ` (${get(product, "sku")})`
          }
          photo={get(product, "photo", {})}
        />
        <Breadcrumbs {...breadcrumbs} />
        <div className={get(styles, "row")}>
          <div className={[get(styles, "col"), get(styles, "col-6")].join(" ")}>
            <div className={get(styles, "mobile-title")}>{product?.name}</div>
            <div className={get(styles, "gallery")}>
              <div className={get(styles, "main-image")}>
                <div className={styles?.desktopGallery}>
                  <Gallery
                    titleImage={get(product, "photo", null)}
                    images={get(product, "gallery", [])}
                    showThumbnails
                    options={{
                      imageMainProps: {
                        useNextImage: true,
                        size: "680x440",
                      },
                      imageThumbnailProps: {
                        useNextImage: true,
                        size: "680x440",
                      },
                    }}
                  />
                </div>
                <div className={styles?.mobileGallery}>
                  <MobileGallery
                    titleImage={get(product, "photo", null)}
                    images={get(product, "gallery", [])}
                    showThumbnails
                    options={{
                      imageMainProps: {
                        useNextImage: true,
                        size: "680x440",
                      },
                      imageThumbnailProps: {
                        useNextImage: true,
                        size: "680x440",
                      },
                    }}
                  />
                </div>

                {!isUnavailable ? (
                  <ProductLabels {...product} isBig={true} />
                ) : (
                  <ProductLabels
                    {...{
                      status: `${upperFirst(t("product:sold out"))}!`,
                      urlTitle: get(product, "urlTitle"),
                      options: { background: "black" },
                    }}
                    isBig={true}
                  />
                )}
              </div>
            </div>
            <div className={styles.hiddenSm}>
              <ProductPriceVariants
                parameters={get(product, "detailedParameters", [])}
                wrapperClassName={styles["info-area"]}
                categories={product?.categories}
              />
            </div>
            {!isUnavailable ? (
              <div className={styles.actionButtons}>
                {/* <WishlistButton productId={product?.id} /> */}
                {/* <CompareButton productId={product?.id} /> */}
              </div>
            ) : (
              ""
            )}
          </div>
          <div className={[get(styles, "col"), get(styles, "col-6")].join(" ")}>
            <h1 className={styles.title}>{product?.name}</h1>
            <div className={get(styles, "product-details")}>
              <span>
                <strong>{product?.brand?.name}</strong>
              </span>
              {product?.partNo ? (
                <span>
                  {upperFirst(t("product:part number"))}: {product?.partNo}
                </span>
              ) : (
                ""
              )}
              {product?.sku ? (
                <span>
                  {upperFirst(t("product:product code"))}: {product?.sku}
                </span>
              ) : (
                ""
              )}
              {product?.ean ? (
                <span>
                  {capitalize(t("product:ean"))}: {product?.ean}
                </span>
              ) : (
                ""
              )}
            </div>

            {get(product, "shortProductDescription.description") ? (
              <div
                className={get(styles, "short-description")}
                dangerouslySetInnerHTML={{
                  __html: get(product, "shortProductDescription.description"),
                }}
              />
            ) : (
              ""
            )}

            <Variant
              data={get(product, "crosssaleProducts", [])}
              hash={"color_variant"}
            />

            <Variant
              data={get(product, "crosssaleProducts", [])}
              hash={"capacity_variant"}
            />

            <div className={get(styles, "product-info")}>
              {!isUnavailable ? (
                <>
                  <div className={styles["info-area"]}>
                    <TimeToArrival product={product} styles={styles} />
                    {/* <Retails product={product} styles={styles} /> */}

                    {round(product?.warrantyDuration / 365) ? (
                      <div className={styles.row}>
                        <div className={`${styles.col} ${styles.label}`}>
                          {upperFirst(t("product:warranty"))}:
                        </div>
                        <div className={`${styles.col} ${styles.value}`}>
                          <span className={styles.warranty}>
                            {round(product?.warrantyDuration / 365) == 1
                              ? t("common:1 year")
                              : `${round(
                                  product?.warrantyDuration / 365
                                )} ${t("common:years")}`}
                          </span>
                        </div>
                      </div>
                    ) : (
                      ""
                    )}

                    {get(product, "condition") ? (
                      <div className={styles.row}>
                        <div className={`${styles.col} ${styles.label}`}>
                          {upperFirst(t("common:condition"))}:
                        </div>
                        <div className={`${styles.col} ${styles.value}`}>
                          <span className={styles.warranty}>
                            <ProductCondition
                              condition={get(product, "condition")}
                            />
                          </span>
                        </div>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>

                  
                  {get(product, "finalPriceData.vat", 20) == 0 ? (
                      <NoVATMessage />
                  ) : (
                    ""
                  )}

                  <div className={`${styles["call-to-action"]}`}>
                    <div
                      className={`${styles.prices}`}
                      style={{ padding: "15px" }}
                    >
                      <b>{upperFirst(t("product:your price with VAT"))}</b>{" "}
                      <br />
                      <span className={get(styles, "final-price")}>
                        {" "}
                        {get(product, "finalPriceData.priceFormatted")}
                      </span>
                      {get(product, "standardPriceData.price", 0) > 0 &&
                      get(product, "standardPriceData.price", 0) >
                        get(product, "finalPriceData.price", 0) ? (
                        <div style={{ marginTop: "15px" }}>
                          {upperFirst(t("product:original price with VAT"))}
                          <span className={get(styles, "origin-price")}>
                            {get(product, "standardPriceData.priceFormatted")}
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className={`${styles.actions}`}>
                      {product?.discount ? (
                        <div className={styles.discount}>
                          {upperFirst(t("common:discount"))} {product.discount}
                        </div>
                      ) : (
                        ""
                      )}
                      <div>
                        <AddToCartButton
                          stores={get(product, "store", [])}
                          product={product}
                          options={{
                            eecProduct: {
                              name: get(product, "name"),
                              id: get(product, "id"),
                              finalPriceData: get(product, "finalPriceData"),
                              brand: get(product, "brand"),
                              categories: get(product, "categories", []),
                            },
                          }}
                        />
                        <ProductQuatro
                          price={get(product, "finalPriceData.price")}
                        />
                      </div>
                    </div>
                  </div>

                  {!isEmpty(product?.productHooks) ? (
                    <div className={get(styles, "product-info")}>
                      <div className={styles["info-area"]}>
                        <Hook
                          type={1}
                          data={product?.productHooks}
                          options={{ title: "K nákupu získate" }}
                        />
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </>
              ) : (
                <b
                  style={{
                    fontSize: "2.5em",
                    marginBottom: "30px",
                    display: "block",
                  }}
                >
                  {upperFirst(t("we are sorry, this product is sold out"))}.
                </b>
              )}
            </div>

            <div className={styles.visibleSm}>
              <ProductPriceVariants
                parameters={get(product, "detailedParameters", [])}
                wrapperClassName={styles["info-area"]}
                categories={product?.categories}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#f7f7f7",
          margin: "15px auto",
        }}
      >
        <div className={styles.container}>
          <div className={styles.col}>
            {!isUnavailable ? (
              <CrossSale data={get(product, "crosssaleProducts", [])} />
            ) : (
              <AlternativeProducts product={product} />
            )}
          </div>
        </div>
      </div>

      <div className={styles.container}>
        <div className={get(styles, "product-advanced")}>
          <div className={get(styles, "tabs-wrapper")}>
            <div className={get(styles, "tabs")}>
              <a
                className={`${styles.tab} ${
                  showedProductBlock == "description" ? styles.active : ""
                }`}
                onClick={function () {
                  setShowedProductBlock("description");
                }}
              >
                {upperFirst(t("product:description"))}
              </a>
              <a
                className={`${styles.tab} ${
                  showedProductBlock == "parameters" ? styles.active : ""
                }`}
                onClick={function () {
                  setShowedProductBlock("parameters");
                }}
              >
                {upperFirst(t("product:parameters"))}
              </a>
            </div>
            <div className={styles.row}>
              <div
                className={`${styles.col} ${get(styles, "col-8")} ${get(
                  styles,
                  "tab-content"
                )} ${
                  showedProductBlock == "description"
                    ? get(styles, "active")
                    : ""
                } ${get(styles, "description")}`}
              >
                <h2>{upperFirst(t("product:description"))}</h2>
                <div
                  dangerouslySetInnerHTML={{
                    __html: replace(
                      get(product, "productDescription.description"),
                      new RegExp('"/images', "gi"),
                      '"https://static.mobilonline.sk/images'
                    ),
                  }}
                />
              </div>
              <div
                className={`${styles.col} ${get(styles, "col-4")} ${get(
                  styles,
                  "tab-content"
                )} ${
                  showedProductBlock == "parameters"
                    ? get(styles, "active")
                    : ""
                } ${get(styles, "parameters")}`}
              >
                <h2>{upperFirst(t("product:technical parameters"))}</h2>

                {map(groupedParameters, (group, label) => {
                  return (
                    <div
                      key={`detailed-parameter-wrapper-${
                        group.length
                      }-${encodeURIComponent(label)}`}
                    >
                      {label != "null" ? <h3>{label}</h3> : ""}
                      <table>
                        <tbody>
                          {map(
                            groupBy(
                              group,
                              (groupItem) => groupItem.parameterId
                            ),
                            (item, index) => {
                              let groupped = [];

                              map(item, (row) =>
                                groupped.push(get(row, "value"))
                              );

                              return (
                                <tr
                                  key={`detailed-parameter-${index}-${groupped.length}`}
                                >
                                  <td>{get(item, "[0].name")}</td>
                                  <td>{groupped.join(", ")}</td>
                                </tr>
                              );
                            }
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export async function getServerSideProps(context) {
  const productPageProps = productPage.serverSideProps(context);
  return {
    props: {
      ...(await productPageProps).props,
      ...await serverSideTranslations(context?.locale, ['common', 'product']),
    }
  }
}

export default Product;
